import React from 'react'

import { useDashboardFeatureFlags, useDashboardPermissions } from '@components'
import { Button } from '@fourthwall/components'
import {
  ApparelIcon,
  ButtonIcon,
  CommentIcon,
  DocumentDownloadIcon,
  MoneyIcon,
  ProfileIcon,
  TagIcon,
} from '@fourthwall/icons'

import { SubscriptionPerk } from '../../../../components/SubscriptionPerk/SubscriptionPerk'
import { useTopSubscriptionPlan } from '../../../../hooks/useTopSubscriptionPlan'
import { formatSubscriptionBytes } from '../../../../utils/formatSubscriptionBytes'
import {
  getSubscriptionPrice,
  getSubscriptionSamplesCreditGrant,
} from '../../../../utils/getSubscriptionPeriodValue'
import * as S from './IntroStage.styled'

export type IntroStageProps = {
  onNext: () => void
  onClose: () => void
}

export const IntroStage = ({ onNext, onClose }: IntroStageProps) => {
  const { hasPermission } = useDashboardPermissions()
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()
  const canUserUpgradeSubscription = hasPermission('subscriptions.edit')
  const { topSubscriptionPlan: topSubscriptionPlanMonthly, getTopSubscriptionPlanLimitLabel } =
    useTopSubscriptionPlan()
  const { topSubscriptionPlan: topSubscriptionPlanYearly } = useTopSubscriptionPlan({
    recurringPeriod: 'YEARLY',
  })

  const getTitle = () => {
    if (!canUserUpgradeSubscription) {
      return "Your account doesn't have permission to upgrade to Pro. Please contact the shop owner to upgrade."
    }

    return `Grow your brand with Fourthwall Pro for ${getSubscriptionPrice({
      subscriptionPlan: topSubscriptionPlanMonthly,
      options: {
        minimumFractionDigits: 2,
      },
    })} or only ${getSubscriptionPrice({
      subscriptionPlan: topSubscriptionPlanYearly,
      options: {
        minimumFractionDigits: 2,
      },
    })}.`
  }

  const renderButton = () => {
    if (!canUserUpgradeSubscription) {
      return <Button label="Close" appearance="primary" fullWidth onClick={onClose} />
    }

    return <Button label="Upgrade to Pro" appearance="primary" fullWidth onClick={onNext} />
  }

  const getStorageLimit = () => {
    const digitalStorageLimit = topSubscriptionPlanMonthly?.digitalProductsStorageLimitBytes || 0
    const productDesignerStorageLimit = dashboardFeatureFlags
      .subscriptionsProductDesignerStorageLimits.enabled
      ? topSubscriptionPlanMonthly?.productDesignerArtworkStorageLimitBytes || 0
      : 0

    return formatSubscriptionBytes(digitalStorageLimit + productDesignerStorageLimit)
  }

  return (
    <>
      <S.Content>
        <S.Text>{getTitle()}</S.Text>
        <S.SecondaryText>What’s included in Fourthwall Pro?</S.SecondaryText>
        <S.PerksContainer>
          <SubscriptionPerk icon={MoneyIcon}>
            {getSubscriptionSamplesCreditGrant({
              subscriptionPlan: topSubscriptionPlanMonthly,
            })}{' '}
            of sample credit
          </SubscriptionPerk>
          <SubscriptionPerk icon={ButtonIcon}>Free domain of your choice</SubscriptionPerk>
          <SubscriptionPerk icon={TagIcon}>No fees on digital products</SubscriptionPerk>
          <SubscriptionPerk icon={ProfileIcon}>
            {getTopSubscriptionPlanLimitLabel('members', true)} team members
          </SubscriptionPerk>
          <SubscriptionPerk icon={ApparelIcon}>
            {getTopSubscriptionPlanLimitLabel('offers', true)} products
          </SubscriptionPerk>
          {dashboardFeatureFlags.subscriptionsStorageLimits.enabled && (
            <SubscriptionPerk icon={DocumentDownloadIcon}>
              {getStorageLimit()} of file storage
            </SubscriptionPerk>
          )}
          <SubscriptionPerk icon={CommentIcon}>Priority customer support</SubscriptionPerk>
        </S.PerksContainer>
      </S.Content>
      <S.ButtonWrapper>{renderButton()}</S.ButtonWrapper>
    </>
  )
}
